import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, Sparkles, ArrowRight, Calendar, Clock, Users, CalendarClock } from 'lucide-react';
import { toast } from 'sonner';
import useReveal from '../hooks/useReveal';
import { cohortCheckout } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';

const includes = [
  'Live cohort learning with Rajni',
  'AI for Product Managers curriculum',
  'World-class PM foundations',
  'Hands-on projects and real feedback',
  'Private community cohort channel',
  'Certificate of completion',
];

const details = [
  { icon: CalendarClock, label: 'Enroll by', value: 'October 10' },
  { icon: Calendar, label: 'Cohort starts', value: 'October 18' },
  { icon: Clock, label: 'Format', value: '6 weeks · 2 hrs weekly, live' },
  { icon: Users, label: 'Cohort size', value: 'Small group · limited seats' },
];

export default function Enroll() {
  const ref = useReveal();
  const navigate = useNavigate();
  const { user, login } = useAuth();
  const [form, setForm] = useState({ name: '', email: '', background: '' });
  const [loading, setLoading] = useState(false);

  React.useEffect(() => {
    if (user) setForm((f) => ({ ...f, name: f.name || user.name || '', email: f.email || user.email || '' }));
  }, [user]);

  const enroll = async () => {
    if (!form.name || !form.email) {
      toast.error('Please add your name and email.');
      return;
    }
    setLoading(true);
    try {
      const res = await cohortCheckout({ ...form, origin_url: window.location.origin });
      window.location.href = res.checkout_url;
    } catch (e) {
      toast.error('Could not start checkout. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div ref={ref} className="max-w-6xl mx-auto px-5 sm:px-8 pt-12">
      <div className="grid md:grid-cols-2 gap-10">
        <div className="reveal in">
          <span className="eyebrow text-[#7C3AED] flex items-center gap-2"><Sparkles size={15} /> Next cohort · AI for Product Managers</span>
          <h1 className="font-display font-extrabold text-[#0E1726] text-[38px] sm:text-[48px] tracking-tight leading-[1.03] mt-4">
            Join the AI for PMs cohort.
          </h1>
          <div className="inline-flex items-center gap-2 mt-4 bg-[#F59E0B]/12 text-[#B45309] rounded-full px-4 py-1.5 text-sm font-semibold">
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full rounded-full bg-[#F59E0B] opacity-75 animate-ping" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-[#F59E0B]" />
            </span>
            Limited seats available
          </div>
          <p className="text-[#5B6472] text-[17px] leading-relaxed mt-5">
            A compact, applied program that helps PMs and builders understand AI, make stronger product decisions,
            and ship with confidence—taught live in a small cohort.
          </p>

          <div className="grid sm:grid-cols-2 gap-3 mt-7">
            {details.map((d) => (
              <div key={d.label} className="flex items-center gap-3 bg-white rounded-xl p-4 shadow-[0_20px_50px_-46px_rgba(14,23,38,0.4)]">
                <span className="w-10 h-10 rounded-full bg-[#7C3AED]/12 text-[#7C3AED] flex items-center justify-center shrink-0"><d.icon size={18} /></span>
                <div>
                  <div className="text-xs text-[#5B6472]">{d.label}</div>
                  <div className="font-semibold text-[#0E1726] text-[15px]">{d.value}</div>
                </div>
              </div>
            ))}
          </div>
          <ul className="mt-7 space-y-3">
            {includes.map((i) => (
              <li key={i} className="flex items-center gap-3 text-[#0E1726]">
                <span className="w-6 h-6 rounded-full bg-[#7C3AED]/12 text-[#7C3AED] flex items-center justify-center"><Check size={14} /></span>
                {i}
              </li>
            ))}
          </ul>
        </div>

        <div className="reveal in bg-white rounded-[24px] p-7 sm:p-9 shadow-[0_30px_80px_-45px_rgba(14,23,38,0.4)]">
          <div className="flex items-baseline gap-2">
            <span className="font-display font-extrabold text-[#0E1726] text-[40px]">$499</span>
            <span className="text-[#5B6472]">one-time · USD</span>
          </div>
          <p className="text-[#5B6472] text-sm mt-1">Secure payment via Stripe. Enroll by <span className="font-semibold text-[#0E1726]">October 10</span> · cohort starts October 18.</p>

          {!user && (
            <button onClick={login} className="mt-6 w-full border border-black/15 rounded-full py-3 font-semibold text-[#0E1726] hover:bg-black/5 transition-colors">
              Sign in to autofill your details
            </button>
          )}

          <div className="mt-5 space-y-4">
            <div>
              <label className="text-sm font-semibold">Full name</label>
              <Input className="mt-1.5 h-11" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your name" />
            </div>
            <div>
              <label className="text-sm font-semibold">Email</label>
              <Input className="mt-1.5 h-11" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@company.com" />
            </div>
            <div>
              <label className="text-sm font-semibold">What do you want from the cohort? (optional)</label>
              <Textarea className="mt-1.5 min-h-[90px]" value={form.background} onChange={(e) => setForm({ ...form, background: e.target.value })} placeholder="A little about your role and goals..." />
            </div>
          </div>

          <button onClick={enroll} disabled={loading} data-cta="Enroll" className="mt-6 w-full bg-[#7C3AED] text-white font-semibold py-3.5 rounded-full flex items-center justify-center gap-2 hover:bg-[#6d28d9] transition-colors disabled:opacity-60">
            {loading ? 'Redirecting to checkout...' : 'Enroll & pay'} <ArrowRight size={18} />
          </button>
          <button onClick={() => navigate('/programs')} className="mt-3 w-full text-[#5B6472] text-sm hover:text-[#0E1726]">Back to programs</button>
        </div>
      </div>
    </div>
  );
}
